import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Input,
  Label,
  Table,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

const MACHINE_ID = 74;

const MonthlyRequirementForm = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [requirements, setRequirements] = useState([]);

  const [form, setForm] = useState({
    part: "",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    required_quantity: "",
  });

  /* ---------------- FETCH PARTS ---------------- */
  useEffect(() => {
    axios
      .get(`https://api.hanoch.in/api/mcc-parts/?machine_id=${MACHINE_ID}`)
      .then((res) => setParts(res.data || []))
      .catch(() => toast.error("Failed to load parts"));
  }, []);

  /* ---------------- FETCH REQUIREMENTS LIST ---------------- */
  const fetchRequirements = () => {
    if (!form.year) return;

    setListLoading(true);
    axios
      .get(
        `https://api.hanoch.in/api/mcc/monthly-requirements/?year=${form.year}&machine_id=${MACHINE_ID}`
      )
      .then((res) => {
        setRequirements(res.data || []);
      })
      .catch(() => {
        toast.error("Failed to load monthly requirements");
        setRequirements([]);
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    fetchRequirements();
  }, [form.year]);

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    if (!form.part || !form.year || !form.month || !form.required_quantity) {
      toast.error("Fill all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        "https://api.hanoch.in/api/mcc/monthly-requirements/",
        {
          part: Number(form.part),
          year: Number(form.year),
          month: Number(form.month),
          required_quantity: Number(form.required_quantity),
        }
      );

      toast.success("Monthly requirement saved");

      setForm((p) => ({
        ...p,
        required_quantity: "",
      }));

      fetchRequirements(); // 🔄 refresh right-side list
    } catch (err) {
      toast.error("Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Row>
      {/* ------------ LEFT : FORM ------------ */}
      <Col xl={6}>
        <Card>
          <CardHeader>Monthly Requirement</CardHeader>
          <CardBody>
            <Row className="mb-3">
              <Col>
                <Label>Part</Label>
                <Input
                  type="select"
                  name="part"
                  value={form.part}
                  onChange={handleChange}
                >
                  <option value="">Select Part</option>
                  {parts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.part_number} - {p.part_name}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Label>Year</Label>
                <Input
                  type="number"
                  name="year"
                  value={form.year}
                  onChange={handleChange}
                />
              </Col>

              <Col md={6}>
                <Label>Month</Label>
                <Input
                  type="select"
                  name="month"
                  value={form.month}
                  onChange={handleChange}
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <Label>Required Quantity</Label>
                <Input
                  type="number"
                  name="required_quantity"
                  placeholder="Enter quantity"
                  value={form.required_quantity}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <Button
              color="success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : "Submit"}
            </Button>
          </CardBody>
        </Card>
      </Col>

      {/* ------------ RIGHT : LIST ------------ */}
      <Col xl={6}>
        <Card>
          <CardHeader>
            Monthly Requirements – {form.year}
          </CardHeader>
          <CardBody style={{ maxHeight: 420, overflowY: "auto" }}>
            {listLoading ? (
              <div className="text-center">
                <Spinner size="sm" /> Loading...
              </div>
            ) : requirements.length === 0 ? (
              <p className="text-muted text-center mb-0">
                No data available
              </p>
            ) : (
              <Table bordered size="sm" hover>
                <thead className="table-light">
                  <tr>
                    <th>Part</th>
                    <th>Month</th>
                    <th className="text-end">Required Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.part_number} - {r.part_name}
                      </td>
                      <td>
                        {new Date(0, r.month - 1).toLocaleString(
                          "default",
                          { month: "short" }
                        )}
                      </td>
                      <td className="text-end">
                        {r.required_quantity.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default MonthlyRequirementForm;
